import { useState } from "react";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { api, setSession, type SignedInUser } from "../lib/api";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ?? "";

export function LoginScreen({ onSignedIn }: { onSignedIn: (u: SignedInUser) => void }): JSX.Element {
  const [stage, setStage] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogle(cred: CredentialResponse): Promise<void> {
    setError(null);
    if (!cred.credential) {
      setError("google_no_credential");
      return;
    }
    setBusy(true);
    try {
      const { token, user } = await api.authGoogle(cred.credential);
      setSession(token, user);
      onSignedIn(user);
    } catch (err) {
      setError((err as Error).message ?? "google_failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleRequest(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.authRequest(email.trim().toLowerCase());
      setStage("code");
    } catch (err) {
      setError((err as Error).message ?? "request_failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { token, user } = await api.authVerify(email.trim().toLowerCase(), code.trim());
      setSession(token, user);
      onSignedIn(user);
    } catch (err) {
      setError((err as Error).message ?? "verify_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <p className="eyebrow">Sign in</p>
        <h1 className="login-h1">
          <span className="word-locker">Locker</span>
          <span className="word-edu">EDU</span>
        </h1>
        <p className="lede">Pilot teachers and admins only. Sign in with Google or get a code by email.</p>

        {GOOGLE_CLIENT_ID && (
          <div className="google-signin-row">
            <GoogleLogin
              onSuccess={handleGoogle}
              onError={() => setError("google_failed")}
              useOneTap={false}
              theme="outline"
              size="large"
              width="320"
            />
            <div className="auth-divider"><span>or</span></div>
          </div>
        )}

        {stage === "email" && (
          <form onSubmit={handleRequest}>
            <label htmlFor="email">School email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@school.org"
            />
            <button className="btn-primary" type="submit" disabled={busy}>
              {busy ? "Sending…" : "Send code →"}
            </button>
          </form>
        )}

        {stage === "code" && (
          <form onSubmit={handleVerify}>
            <p className="callout">
              Code sent to <strong>{email}</strong>. Check your inbox.
            </p>
            <label htmlFor="code">6-digit code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              autoFocus
            />
            <button className="btn-primary" type="submit" disabled={busy || code.length !== 6}>
              {busy ? "Verifying…" : "Verify →"}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setStage("email");
                setCode("");
                setError(null);
              }}
            >
              ← Use a different email
            </button>
          </form>
        )}

        {error && <p className="error">{labelError(error)}</p>}
      </div>
    </div>
  );
}

function labelError(code: string): string {
  switch (code) {
    case "invalid_email":
      return "That doesn't look like an email address.";
    case "invalid_code":
    case "code_expired":
      return "Code is wrong or expired. Try requesting a new one.";
    case "code_used":
      return "That code was already used. Request a fresh one.";
    case "user_not_found":
      return "No LockerEDU account for that email yet. Email pilot@lockeredu.com.";
    case "unauthorized":
      return "Session expired. Please sign in again.";
    case "email_not_verified":
      return "Google says that email isn't verified. Verify it on Google or sign in via the email-code flow.";
    case "email_linked_to_different_google_account":
      return "This email is already linked to a different Google account. Email pilot@lockeredu.com.";
    case "google_no_credential":
    case "google_failed":
      return "Google sign-in didn't complete. Try again or use the email-code flow below.";
    default:
      return `Sign-in failed (${code}).`;
  }
}
